import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  buildStationTimingEmptySelection,
  IStationTimingPreviewSelection,
  STATION_TIMING_PREVIEW_MODAL_WIDTH,
  StationTimingPreview,
} from "@ryde/controls";
import { useModalConfirm, useStationRouteDtoActions } from "@ryde/redux";
import {
  collapseOptionalRouteLegsAcrossWaypoints,
  ConfirmResult,
  errors,
  expandCollapsedRouteLegsAcrossWaypoints,
  filterRealRouteStations,
  getStationRoutePlannedLegSeconds,
  IStationRouteDto,
  Log,
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
  STATION_TIMING_SOURCE_STATION_ROUTE,
  stationRouteHasUnsavedStationsChangeAtom,
  StationTimingApi,
  updateStationRouteComputedDataStationsTimeBulk,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useStationRouteMeasuredTimes = (
  stationRouteDto: Partial<IStationRouteDto> | undefined,
) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { confirmDialogResult } = useModalConfirm();
  const { updateEditingStationRouteComputedData } = useStationRouteDtoActions();
  const setStationRouteHasUnsavedStationChange = useSetRecoilState(
    stationRouteHasUnsavedStationsChangeAtom,
  );
  const [isLoading, setIsLoading] = useState(false);
  // `isLoading` state only reaches this closure on the next render, so a second click inside the same
  // tick would fire a second request; the ref is what actually keeps the flow single-flight.
  const isRequestInFlightRef = useRef(false);
  const selectionRef = useRef<IStationTimingPreviewSelection>(
    buildStationTimingEmptySelection(),
  );

  const handleSelectionChange = useEvent(
    (selection: IStationTimingPreviewSelection): void => {
      selectionRef.current = selection;
    },
  );

  const openMeasuredTimes = useEvent(async (): Promise<void> => {
    const stations = stationRouteDto?.computedData?.stationInfos || [];
    const realStations = filterRealRouteStations(stations);

    if (
      !stationRouteDto ||
      realStations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION
    ) {
      Log.warnAndToast(t(errors.stationTiming.notEnoughStations));
      return;
    }

    if (isRequestInFlightRef.current) {
      return;
    }

    isRequestInFlightRef.current = true;
    setIsLoading(true);
    // An ML path pools every day of the week into one number, so it asks for the per-day breakdown that
    // shows whether Friday traffic is really the Monday traffic the single number implies.
    const statsResult = await StationTimingApi.getStationTimingPairStats({
      sourceEntity: STATION_TIMING_SOURCE_STATION_ROUTE,
      stationIds: realStations.map(station => station.id),
      shouldIncludeDayOfWeek: true,
    });
    setIsLoading(false);
    isRequestInFlightRef.current = false;

    if (!statsResult.success) {
      warnAndToastSimpleResult(statsResult, t);
      return;
    }

    const stats = statsResult.data;

    if (!stats) {
      Log.warnAndToast(t(errors.stationTiming.statsNotLoaded));
      return;
    }

    selectionRef.current = buildStationTimingEmptySelection();

    const confirmationResult = await confirmDialogResult({
      title: t("stationTiming.MEASURED_TIMES_TITLE"),
      okButtonTitle: t("stationTiming.MEASURED_TIMES_APPLY"),
      width: STATION_TIMING_PREVIEW_MODAL_WIDTH,
      customContent: (
        <StationTimingPreview
          stations={realStations.map(station => ({
            id: station.id,
            name: station.name,
          }))}
          plannedLegSeconds={
            collapseOptionalRouteLegsAcrossWaypoints({
              stations,
              legs: getStationRoutePlannedLegSeconds({
                stationRouteDto,
                timezone,
              }),
            }) ?? []
          }
          stats={stats}
          onChange={handleSelectionChange}
          resolutionNote={t("stationTiming.WHOLE_MINUTE_RESOLUTION_NOTE")}
        />
      ),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const selection = selectionRef.current;

    if (!selection.selectedLegIndexes.length) {
      Log.warnAndToast(t(errors.stationTiming.noMeasurementSelected));
      return;
    }

    // Applied to the edited form only: skipApiCheck keeps a route recount from overwriting the measured
    // times, and the unsaved flag keeps Save enabled until the user persists the ML path.
    const computedData = updateStationRouteComputedDataStationsTimeBulk({
      stationRouteDto,
      legSeconds:
        expandCollapsedRouteLegsAcrossWaypoints({
          stations,
          collapsedLegs: selection.legSeconds,
        }) ?? selection.legSeconds,
      timezone,
    });
    const result = await updateEditingStationRouteComputedData({
      computedData,
      direction: stationRouteDto.metadata?.direction,
      skipApiCheck: true,
    });

    if (!result.success) {
      warnAndToastSimpleResult(result, t);
      return;
    }

    setStationRouteHasUnsavedStationChange(true);
    Log.infoAndToast(
      `${t("stationTiming.MEASURED_TIMES_APPLIED")} ${t("stationTiming.WHOLE_MINUTE_RESOLUTION_NOTE")}`,
    );
  });

  return {
    isLoading,
    openMeasuredTimes,
  };
};
